/**
 * WeighmentTab.jsx
 * NChat — Real-time Weighment Integration UI
 *
 * Features:
 *  - Polls /weighment.php?action=poll every 4s for live updates
 *  - Lists weighments with linked auto-generated bills
 *  - Delete weighment (cascades bill) with confirmation
 *  - Delete bill only (keeps weighment, resets status)
 *  - Mark bill as paid
 *  - Summary stats bar
 *  - Filter by status
 *  - Toast notifications for real-time arrivals
 *  - Device token management panel
 */
import React, {
  useEffect, useState, useCallback, useRef, useReducer,
} from 'react';
import { useChat } from '../../context/ChatContext';
import { weighmentAPI } from '../../utils/api';
import { 
  fmtKg, fmtCurrency, formatDate as fmtDate, formatDateOnly as fmtDateOnly, formatTimeOnly as fmtTimeOnly 
} from '../../utils/helpers';

// ─── Constants ─────────────────────────────────────────────────────────────
const POLL_INTERVAL = 4000;   // 4 seconds
const TOAST_DURATION = 5000;  // 5 seconds

const W_STATUS = {
  pending: { bg: '#FFF7E6', text: '#D97706', dot: '#F59E0B', label: 'Pending' },
  billed: { bg: '#EFF6FF', text: '#2563EB', dot: '#3B82F6', label: 'Billed' },
  cancelled: { bg: '#F3F4F6', text: '#6B7280', dot: '#9CA3AF', label: 'Cancelled' },
};

const B_STATUS = {
  draft: { bg: '#F3F4F6', text: '#6B7280', label: 'Draft' },
  issued: { bg: '#FFF7E6', text: '#D97706', label: 'Issued' },
  paid: { bg: '#ECFDF5', text: '#059669', label: 'Paid' },
  cancelled: { bg: '#FEF2F2', text: '#DC2626', label: 'Cancelled' },
};

const TABS = [
  { id: 'weighments', label: '⚖️ Weighments' },
  { id: 'bills', label: '🧾 Bills' },
];
// ─── Main component ──────────────────────────────────────────────────────────
export default function WeighmentTab() {
  const { weighmentUnread, setWeighmentUnread } = useChat();
  const [activeTab, setActiveTab] = useState('weighments');
  // Polling moved to ChatContext.jsx for global background support

  const addToast = useCallback((message, type = 'info') => {
    window.dispatchEvent(new CustomEvent('app:notification', {
      detail: { message, type, title: type === 'success' ? '⚖️ Weighment' : 'ℹ️ Notification' }
    }));
  }, []);

  // Clear unread status when this tab is opened
  useEffect(() => {
    if (weighmentUnread) setWeighmentUnread(false);
  }, [weighmentUnread, setWeighmentUnread]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#F9FAFB', overflow: 'hidden' }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', background: 'white', borderBottom: '1px solid #E5E7EB', flexShrink: 0 }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              flex: 1, padding: '11px 4px', border: 'none', background: 'none',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              color: activeTab === t.id ? '#128C7E' : '#6B7280',
              borderBottom: activeTab === t.id ? '2px solid #128C7E' : '2px solid transparent',
              transition: 'color .15s',
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {activeTab === 'weighments' && <WeighmentsPanel addToast={addToast} />}
        {activeTab === 'bills' && <BillsPanel addToast={addToast} />}
      </div>
    </div>
  );
}

// ─── Weighments Panel ────────────────────────────────────────────────────────
function WeighmentsPanel({ addToast }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    try {
      const params = { _t: Date.now() }; // Cache busting
      if (filter !== 'all') params.status = filter;
      if (search) params.search = search;
      const res = await weighmentAPI.list(params);
      if (res.data.success) {
        setRows(res.data.weighments || []);
        if (res.data.summary) setSummary(res.data.summary);
        setTotal(res.data.total || (res.data.weighments ? res.data.weighments.length : 0));
      }
    } catch (e) {
      console.error('Failed to load weighments:', e);
      addToast('Failed to load weighments', 'error');
    }
    setLoading(false);
  }, [filter, search, addToast]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  // Listen to real-time events
  useEffect(() => {
    const onNew = (e) => {
      console.log('Real-time weighment event received!', e.detail);
      const newItem = e.detail;
      if (newItem && newItem.id) {
        setRows(prev => {
          if (prev.find(r => r.id === newItem.id)) return prev;
          const row = { ...newItem, net_weight: newItem.net_weight || (newItem.gross_weight - newItem.tare_weight) };
          setTotal(t => t + 1);
          return [row, ...prev];
        });
      }
      load(); // Fetch full details from server
    };
    const onRefresh = () => load();
    window.addEventListener('weighment:new', onNew);
    window.addEventListener('weighment:deleted', onRefresh);
    window.addEventListener('app:weighment_refresh', onRefresh);
    return () => {
      window.removeEventListener('weighment:new', onNew);
      window.removeEventListener('weighment:deleted', onRefresh);
      window.removeEventListener('app:weighment_refresh', onRefresh);
    };
  }, [load]);

  const handleDelete = async (row) => {
    setDeleting(row.id);
    try {
      await weighmentAPI.delete(row.id);
      setRows(prev => prev.filter(r => r.id !== row.id));
      setSelected(null);
      addToast(`Weighment ${row.ticket_no} deleted`, 'info');
    } catch (e) {
      addToast(e.response?.data?.message ?? 'Delete failed', 'error');
    }
    setDeleting(null);
  };

  const FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'billed', label: 'Billed' },
    { id: 'pending', label: 'Pending' },
    { id: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      {/* Summary */}
      {summary && (
        <div style={{ display: 'flex', gap: 8, padding: '12px 14px 4px', flexShrink: 0 }}>
          <StatCard label="Total" value={summary.total_count} unit="records" color="#128C7E" bg="#F0FDF4" />
          <StatCard label="Billed" value={summary.billed_count} unit="records" color="#2563EB" bg="#EFF6FF" />
          <StatCard label="Net Wt" value={fmtKg(summary.total_net_weight)} color="#7C3AED" bg="#F5F3FF" />
        </div>
      )}

      {/* Search */}
      <div style={{ padding: '8px 14px 4px', flexShrink: 0 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search ticket, vehicle, material…"
          style={{
            width: '100%', boxSizing: 'border-box', padding: '9px 12px',
            borderRadius: 10, border: '1.5px solid #E5E7EB', fontSize: 13,
            background: 'white', outline: 'none',
          }}
        />
      </div>

      {/* Filter chips & Refresh */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px 8px', overflowX: 'auto', flexShrink: 0 }}>
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} style={{
            padding: '5px 13px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            border: filter === f.id ? '2px solid #128C7E' : '2px solid #E5E7EB',
            background: filter === f.id ? '#128C7E' : 'white',
            color: filter === f.id ? 'white' : '#374151',
            cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
          }}>{f.label}</button>
        ))}
        <div style={{ flex: 1 }} />
        <button
          onClick={() => { setLoading(true); load(); }}
          style={{
            padding: '5px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: '#F3F4F6', color: '#128C7E', border: '1.5px solid #E5E7EB',
            cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4
          }}
        >🔄 Refresh</button>
      </div>

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState icon="⚖️" title="No weighments" sub={search ? 'Try a different search' : 'Waiting for data from weighbridge…'} />
      ) : (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {Object.entries(
            rows.reduce((acc, row) => {
              const day = fmtDateOnly(row.weighed_at);
              if (!acc[day]) acc[day] = [];
              acc[day].push(row);
              return acc;
            }, {})
          ).map(([day, dayRows]) => (
            <div key={day}>
              <div style={{
                padding: '10px 14px', fontSize: 11, fontWeight: 800, color: '#667781',
                background: '#F3F4F6', position: 'sticky', top: 0, zIndex: 1,
                textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #E5E7EB'
              }}>
                🗓️ {day}
              </div>
              {dayRows.map(row => (
                <WeighmentCard
                  key={row.id}
                  row={row}
                  onOpen={() => setSelected(row)}
                />
              ))}
            </div>
          ))}
          {total > rows.length && (
            <div style={{ textAlign: 'center', padding: '12px', fontSize: 12, color: '#9CA3AF' }}>
              Showing {rows.length} of {total}
            </div>
          )}
        </div>
      )}

      {selected && (
        <WeighmentDetail
          row={selected}
          onClose={() => setSelected(null)}
          onDelete={() => handleDelete(selected)}
          deleting={deleting === selected.id}
          addToast={addToast}
          onBillPaid={(billId) => {
            setRows(prev => prev.map(r =>
              r.id === selected.id ? { ...r, bill_status: 'paid', paid_at: new Date().toISOString() } : r
            ));
            setSelected(prev => ({ ...prev, bill_status: 'paid', paid_at: new Date().toISOString() }));
          }}
        />
      )}
    </div>
  );
}

function WeighmentCard({ row, onOpen }) {
  const ws = W_STATUS[row.status] || W_STATUS.pending;
  const bs = row.bill_status ? B_STATUS[row.bill_status] : null;
  return (
    <div
      onClick={onOpen}
      style={{
        margin: '0 12px 9px', borderRadius: 12, background: 'white',
        boxShadow: '0 1px 4px rgba(0,0,0,.07)', padding: '13px 14px',
        cursor: 'pointer', borderLeft: `4px solid ${ws.dot}`,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 13, color: '#111B21', marginBottom: 2 }}>
            🎫 {row.ticket_no}
            {row.vehicle_no && <span style={{ fontWeight: 500, color: '#667781', marginLeft: 8 }}>🚛 {row.vehicle_no}</span>}
          </div>
          {row.material && (
            <div style={{ fontSize: 12, color: '#667781', marginBottom: 3 }}>📦 {row.material}</div>
          )}
          <div style={{ fontSize: 11, color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>Net: <strong style={{ color: '#374151' }}>{fmtKg(row.net_weight)}</strong></span>
            {row.location && <span>• 📍 {row.location}</span>}
            <span style={{ color: '#128C7E', fontWeight: 800, background: '#F0FDF4', padding: '1px 6px', borderRadius: 6 }}>
              🕒 {fmtTimeOnly(row.weighed_at)}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, marginLeft: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 10, background: ws.bg, color: ws.text }}>
            {ws.label}
          </span>
          {bs && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: bs.bg, color: bs.text }}>
              Bill: {bs.label}
            </span>
          )}
          {row.total_amount && (
            <div style={{ fontSize: 13, fontWeight: 800, color: '#111B21' }}>
              {fmtCurrency(row.total_amount, row.currency)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WeighmentDetail({ row, onClose, onDelete, deleting, addToast, onBillPaid }) {
  const [paying, setPaying] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const ws = W_STATUS[row.status] || W_STATUS.pending;
  const bs = row.bill_status ? B_STATUS[row.bill_status] : null;

  const handleMarkPaid = async () => {
    if (!row.wb_id) return;
    setPaying(true);
    try {
      await weighmentAPI.markPaid(row.wb_id);
      onBillPaid(row.wb_id);
      addToast('Bill marked as paid ✓', 'success');
    } catch (e) {
      addToast(e.response?.data?.message ?? 'Failed to mark paid', 'error');
    }
    setPaying(false);
  };

  return (
    <BottomSheet onClose={onClose}>
      {/* Status badges */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Badge bg={ws.bg} color={ws.text}>⚖️ {ws.label}</Badge>
        {bs && <Badge bg={bs.bg} color={bs.text}>🧾 Bill: {bs.label}</Badge>}
      </div>

      <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 900, color: '#111B21' }}>
        Ticket #{row.ticket_no}
      </h2>
      {row.vehicle_no && <div style={{ fontSize: 14, color: '#667781', marginBottom: 12 }}>🚛 {row.vehicle_no}</div>}

      {/* Weight breakdown */}
      <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.4px' }}>
          Weight Details
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {[
            { label: 'Gross', value: fmtKg(row.gross_weight) },
            { label: 'Tare', value: fmtKg(row.tare_weight) },
            { label: 'Net', value: fmtKg(row.net_weight), bold: true },
          ].map(w => (
            <div key={w.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#667781' }}>{w.label}</div>
              <div style={{ fontSize: w.bold ? 16 : 13, fontWeight: w.bold ? 900 : 600, color: '#111B21' }}>{w.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Bill details */}
      {row.bill_reference && (
        <div style={{ background: '#EFF6FF', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#2563EB', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.4px' }}>
            Auto-Generated Bill
          </div>
          <Row label="Reference" value={row.bill_reference} />
          <Row label="Rate/kg" value={row.rate_per_kg ? `₹${row.rate_per_kg}` : '—'} />
          <Row label="Tax" value={row.tax_pct ? `${row.tax_pct}%` : '0%'} />
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0', borderTop: '1px solid #BFDBFE', marginTop: 6 }}>
            <span style={{ fontWeight: 700, color: '#1D4ED8' }}>Total Amount</span>
            <span style={{ fontWeight: 900, fontSize: 16, color: '#1D4ED8' }}>{fmtCurrency(row.total_amount, row.currency)}</span>
          </div>
        </div>
      )}

      {/* Info rows */}
      {row.material && <Row label="Material" value={row.material} />}
      {row.location && <Row label="Location" value={row.location} />}
      <Row label="Weighed At" value={fmtDate(row.weighed_at)} />
      <Row label="Received At" value={fmtDate(row.received_at)} />
      {row.due_date && <Row label="Due Date" value={fmtDateOnly(row.due_date)} />}
      {row.paid_at && <Row label="Paid At" value={fmtDate(row.paid_at)} />}
      {row.notes && <Row label="Notes" value={row.notes} />}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
        {/* Mark paid */}
        {row.bill_status && ['issued', 'draft'].includes(row.bill_status) && (
          <button
            onClick={handleMarkPaid}
            disabled={paying}
            style={{
              flex: 1, padding: '13px', borderRadius: 11,
              background: paying ? '#9CA3AF' : '#059669', color: 'white',
              border: 'none', fontSize: 14, fontWeight: 700, cursor: paying ? 'not-allowed' : 'pointer',
            }}
          >{paying ? 'Processing…' : '✓ Mark Paid'}</button>
        )}

        {/* Delete */}
        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            style={{
              flex: row.bill_status && ['issued', 'draft'].includes(row.bill_status) ? 0 : 1,
              padding: '13px', borderRadius: 11,
              background: '#FEF2F2', color: '#DC2626',
              border: '1.5px solid #FCA5A5', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              minWidth: 52,
            }}
          >🗑</button>
        ) : (
          <div style={{ flex: 1, display: 'flex', gap: 6 }}>
            <button
              onClick={onDelete}
              disabled={deleting}
              style={{
                flex: 1, padding: '13px', borderRadius: 11,
                background: deleting ? '#9CA3AF' : '#DC2626', color: 'white',
                border: 'none', fontSize: 13, fontWeight: 700, cursor: deleting ? 'not-allowed' : 'pointer',
              }}
            >{deleting ? '…' : 'Delete + Bill'}</button>
            <button
              onClick={() => setConfirmDelete(false)}
              style={{
                flex: 1, padding: '13px', borderRadius: 11,
                background: '#F3F4F6', color: '#374151',
                border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}
            >Cancel</button>
          </div>
        )}
      </div>
    </BottomSheet>
  );
}

// ─── Bills Panel ─────────────────────────────────────────────────────────────
function BillsPanel({ addToast }) {
  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { _t: Date.now() }; // Cache busting
      if (filter !== 'all') params.status = filter;
      const res = await weighmentAPI.billList(params);
      if (res.data.success) {
        setBills(res.data.data.bills);
        setSummary(res.data.data.summary);
      }
    } catch { }
    setLoading(false);
  }, [filter]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  useEffect(() => {
    const onNewBill = (e) => {
      console.log('Real-time bill event received!', e.detail);
      const newBill = e.detail;
      if (newBill && newBill.id) {
        setBills(prev => {
          if (prev.find(b => b.id === newBill.id)) return prev;
          setSummary(s => s ? {
            ...s,
            issued_count: (parseInt(s.issued_count) || 0) + 1,
            issued_amount: (parseFloat(s.issued_amount) || 0) + (parseFloat(newBill.total_amount) || 0)
          } : s);
          return [newBill, ...prev];
        });
      }
      load();
    };
    const refresh = () => load();
    window.addEventListener('weighment:bill_new', onNewBill);
    window.addEventListener('weighment:bill_deleted', refresh);
    window.addEventListener('weighment:deleted', refresh);
    window.addEventListener('app:bill_refresh', refresh);
    return () => {
      window.removeEventListener('weighment:bill_new', onNewBill);
      window.removeEventListener('weighment:bill_deleted', refresh);
      window.removeEventListener('weighment:deleted', refresh);
      window.removeEventListener('app:bill_refresh', refresh);
    };
  }, [load]);

  const handleDeleteBill = async (bill) => {
    setDeleting(bill.id);
    try {
      await weighmentAPI.billDelete(bill.id);
      setBills(prev => prev.filter(b => b.id !== bill.id));
      setSelected(null);
      addToast(`Bill ${bill.bill_reference} deleted`, 'info');
    } catch (e) {
      addToast(e.response?.data?.message ?? 'Delete failed', 'error');
    }
    setDeleting(null);
  };

  const handleMarkPaid = async (bill) => {
    try {
      await weighmentAPI.markPaid(bill.id);
      setBills(prev => prev.map(b => b.id === bill.id ? { ...b, status: 'paid', paid_at: new Date().toISOString() } : b));
      setSelected(prev => prev?.id === bill.id ? { ...prev, status: 'paid', paid_at: new Date().toISOString() } : prev);
      addToast('Bill marked as paid ✓', 'success');
    } catch (e) {
      addToast(e.response?.data?.message ?? 'Failed', 'error');
    }
  };

  const FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'issued', label: 'Issued' },
    { id: 'paid', label: 'Paid' },
    { id: 'draft', label: 'Draft' },
  ];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      {summary && (
        <div style={{ display: 'flex', gap: 8, padding: '12px 14px 4px', flexShrink: 0 }}>
          <StatCard label="Issued" value={fmtCurrency(summary.issued_amount)} sub={`${summary.issued_count} bills`} color="#D97706" bg="#FFF7E6" />
          <StatCard label="Paid" value={fmtCurrency(summary.paid_amount)} sub={`${summary.paid_count} bills`} color="#059669" bg="#ECFDF5" />
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, padding: '8px 14px', overflowX: 'auto', flexShrink: 0 }}>
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} style={{
            padding: '5px 13px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            border: filter === f.id ? '2px solid #128C7E' : '2px solid #E5E7EB',
            background: filter === f.id ? '#128C7E' : 'white',
            color: filter === f.id ? 'white' : '#374151',
            cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
          }}>{f.label}</button>
        ))}
      </div>

      {loading ? <Spinner /> : bills.length === 0 ? (
        <EmptyState icon="🧾" title="No bills" sub="Bills are auto-generated when weighment data arrives" />
      ) : (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {Object.entries(
            bills.reduce((acc, b) => {
              const day = fmtDateOnly(b.created_at);
              if (!acc[day]) acc[day] = [];
              acc[day].push(b);
              return acc;
            }, {})
          ).map(([day, dayBills]) => (
            <div key={day}>
              <div style={{
                padding: '10px 14px', fontSize: 11, fontWeight: 800, color: '#667781',
                background: '#F3F4F6', position: 'sticky', top: 0, zIndex: 1,
                textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #E5E7EB'
              }}>
                🗓️ {day}
              </div>
              {dayBills.map(bill => {
                const bs = B_STATUS[bill.status] || B_STATUS.issued;
                return (
                  <div
                    key={bill.id}
                    onClick={() => setSelected(bill)}
                    style={{
                      margin: '0 12px 9px', borderRadius: 12, background: 'white',
                      boxShadow: '0 1px 4px rgba(0,0,0,.07)', padding: '13px 14px',
                      cursor: 'pointer', borderLeft: `4px solid ${bs.text}`,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 13, color: '#111B21', marginBottom: 2 }}>
                          {bill.bill_reference}
                        </div>
                        <div style={{ fontSize: 12, color: '#667781', marginBottom: 3 }}>
                          🎫 {bill.ticket_no}
                          {bill.vehicle_no && ` • 🚛 ${bill.vehicle_no}`}
                        </div>
                        <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                          {fmtKg(bill.net_weight_kg)} • {fmtTimeOnly(bill.created_at)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, marginLeft: 8, flexShrink: 0 }}>
                        <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 10, background: bs.bg, color: bs.text }}>
                          {bs.label}
                        </span>
                        <div style={{ fontSize: 14, fontWeight: 900, color: '#111B21' }}>
                          {fmtCurrency(bill.total_amount, bill.currency)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {selected && (
        <BillDetail
          bill={selected}
          onClose={() => setSelected(null)}
          onDelete={() => handleDeleteBill(selected)}
          onMarkPaid={() => handleMarkPaid(selected)}
          deleting={deleting === selected.id}
        />
      )}
    </div>
  );
}

function BillDetail({ bill, onClose, onDelete, onMarkPaid, deleting }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [paying, setPaying] = useState(false);
  const bs = B_STATUS[bill.status] || B_STATUS.issued;

  const doMarkPaid = async () => {
    setPaying(true);
    await onMarkPaid();
    setPaying(false);
  };

  return (
    <BottomSheet onClose={onClose}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 900, color: '#111B21' }}>{bill.bill_reference}</h2>
        <Badge bg={bs.bg} color={bs.text}>{bs.label}</Badge>
      </div>

      <div style={{ fontSize: 28, fontWeight: 900, color: '#111B21', marginBottom: 16 }}>
        {fmtCurrency(bill.total_amount, bill.currency)}
      </div>

      <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
        <Row label="Ticket" value={`#${bill.ticket_no}`} />
        {bill.vehicle_no && <Row label="Vehicle" value={bill.vehicle_no} />}
        {bill.material && <Row label="Material" value={bill.material} />}
        <Row label="Net Weight" value={fmtKg(bill.net_weight_kg)} />
        <Row label="Rate/kg" value={bill.rate_per_kg ? `₹${Number(bill.rate_per_kg).toFixed(4)}` : '—'} />
        <Row label="Tax" value={`${bill.tax_pct}% = ${fmtCurrency(bill.tax_amount)}`} />
      </div>

      <Row label="Issued" value={fmtDate(bill.issued_at)} />
      {bill.due_date && <Row label="Due" value={fmtDateOnly(bill.due_date)} />}
      {bill.paid_at && <Row label="Paid" value={fmtDate(bill.paid_at)} />}

      <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
        {['issued', 'draft'].includes(bill.status) && (
          <button
            onClick={doMarkPaid}
            disabled={paying}
            style={{
              flex: 1, padding: '13px', borderRadius: 11,
              background: paying ? '#9CA3AF' : '#059669', color: 'white',
              border: 'none', fontSize: 14, fontWeight: 700, cursor: paying ? 'not-allowed' : 'pointer',
            }}
          >{paying ? 'Processing…' : '✓ Mark Paid'}</button>
        )}
        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            style={{
              padding: '13px 18px', borderRadius: 11, background: '#FEF2F2',
              color: '#DC2626', border: '1.5px solid #FCA5A5', fontSize: 14,
              fontWeight: 700, cursor: 'pointer',
            }}
          >🗑</button>
        ) : (
          <div style={{ flex: 1, display: 'flex', gap: 6 }}>
            <button
              onClick={onDelete}
              disabled={deleting}
              style={{
                flex: 1, padding: '13px', borderRadius: 11,
                background: deleting ? '#9CA3AF' : '#DC2626', color: 'white',
                border: 'none', fontSize: 13, fontWeight: 700, cursor: deleting ? 'not-allowed' : 'pointer',
              }}
            >{deleting ? '…' : 'Delete Bill'}</button>
            <button
              onClick={() => setConfirmDelete(false)}
              style={{
                flex: 1, padding: '13px', borderRadius: 11,
                background: '#F3F4F6', color: '#374151',
                border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}
            >Cancel</button>
          </div>
        )}
      </div>
    </BottomSheet>
  );
}

// ─── Tokens Panel ─────────────────────────────────────────────────────────────
function TokensPanel({ addToast }) {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [form, setForm] = useState({ label: '', rate_per_kg: '', tax_pct: '', allowed_ips: '' });
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await weighmentAPI.tokenList();
      if (res.data.success) setTokens(res.data.data.tokens);
    } catch { }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.rate_per_kg) { addToast('Rate per kg is required', 'error'); return; }
    setCreating(true);
    try {
      const res = await weighmentAPI.tokenCreate({
        label: form.label || 'Weighbridge',
        rate_per_kg: parseFloat(form.rate_per_kg),
        tax_pct: parseFloat(form.tax_pct || 0),
        allowed_ips: form.allowed_ips || '',
      });
      if (res.data.success) {
        setNewToken(res.data);
        setShowForm(false);
        setForm({ label: '', rate_per_kg: '', tax_pct: '', allowed_ips: '' });
        load();
      }
    } catch (e) {
      addToast(e.response?.data?.message ?? 'Failed', 'error');
    }
    setCreating(false);
  };

  const handleDelete = async (id) => {
    try {
      await weighmentAPI.tokenDelete(id);
      setTokens(prev => prev.filter(t => t.id !== id));
      addToast('Token revoked', 'info');
    } catch (e) {
      addToast(e.response?.data?.message ?? 'Failed', 'error');
    }
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: 14 }}>
      <div style={{ marginBottom: 12, fontSize: 12, color: '#667781', lineHeight: 1.5 }}>
        Device tokens authenticate your weighbridge or ERP system.
        Configure the token in your external system and point it to:<br />
        <code style={{ background: '#F3F4F6', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>
          POST /backend/api/weighment.php?action=create
        </code><br />
        with header <code style={{ background: '#F3F4F6', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>
          X-Weighment-Token: &lt;raw_token&gt;
        </code>
      </div>

      <button
        onClick={() => setShowForm(!showForm)}
        style={{
          padding: '11px', borderRadius: 10, background: '#128C7E', color: 'white',
          border: 'none', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 12,
        }}
      >
        {showForm ? '✕ Cancel' : '+ New Device Token'}
      </button>

      {showForm && (
        <div style={{ background: 'white', borderRadius: 12, padding: 14, marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
          {[
            { key: 'label', label: 'Label', placeholder: 'Main Gate', type: 'text' },
            { key: 'rate_per_kg', label: 'Rate/kg (₹)', placeholder: '5.50', type: 'number' },
            { key: 'tax_pct', label: 'Tax %', placeholder: '18', type: 'number' },
            { key: 'allowed_ips', label: 'Allowed IPs', placeholder: '192.168.1.10,10.0.0.0/24 (blank = any)', type: 'text' },
          ].map(f => (
            <div key={f.key} style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#667781', display: 'block', marginBottom: 4 }}>{f.label}</label>
              <input
                type={f.type}
                value={form[f.key]}
                onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '9px 12px',
                  borderRadius: 8, border: '1.5px solid #E5E7EB', fontSize: 13, outline: 'none',
                }}
              />
            </div>
          ))}
          <button
            onClick={handleCreate}
            disabled={creating}
            style={{
              width: '100%', padding: '12px', borderRadius: 10,
              background: creating ? '#9CA3AF' : '#128C7E', color: 'white',
              border: 'none', fontSize: 14, fontWeight: 700, cursor: creating ? 'not-allowed' : 'pointer',
            }}
          >{creating ? 'Creating…' : 'Create Token'}</button>
        </div>
      )}

      {/* Show newly created token */}
      {newToken && (
        <div style={{ background: '#064E3B', color: 'white', borderRadius: 12, padding: 14, marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>✓ Token created — copy now, shown once!</div>
          <code style={{
            display: 'block', background: 'rgba(255,255,255,.1)', borderRadius: 8,
            padding: '10px', fontSize: 11, wordBreak: 'break-all', marginBottom: 8,
          }}>
            {newToken.raw_token}
          </code>
          <button
            onClick={() => { navigator.clipboard.writeText(newToken.raw_token); addToast('Token copied!', 'success'); }}
            style={{ padding: '8px 14px', borderRadius: 8, background: 'rgba(255,255,255,.2)', color: 'white', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
          >📋 Copy</button>
          <button
            onClick={() => setNewToken(null)}
            style={{ marginLeft: 8, padding: '8px 14px', borderRadius: 8, background: 'transparent', color: 'rgba(255,255,255,.6)', border: '1px solid rgba(255,255,255,.3)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
          >Dismiss</button>
        </div>
      )}

      {loading ? <Spinner /> : tokens.length === 0 ? (
        <EmptyState icon="🔑" title="No tokens" sub="Create a token to connect your weighbridge" />
      ) : (
        tokens.map(t => (
          <div key={t.id} style={{ background: 'white', borderRadius: 12, padding: '12px 14px', marginBottom: 8, boxShadow: '0 1px 4px rgba(0,0,0,.07)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#111B21', marginBottom: 3 }}>{t.label}</div>
                <div style={{ fontSize: 11, color: '#667781' }}>
                  ₹{t.rate_per_kg}/kg • {t.tax_pct}% tax
                  {t.allowed_ips && ` • IP: ${t.allowed_ips}`}
                </div>
                <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>
                  {t.last_used_at ? `Last used: ${fmtDate(t.last_used_at)}` : 'Never used'} • ID #{t.id}
                </div>
              </div>
              <button
                onClick={() => handleDelete(t.id)}
                style={{ padding: '6px 10px', borderRadius: 8, background: '#FEF2F2', color: '#DC2626', border: 'none', fontSize: 12, cursor: 'pointer', fontWeight: 700 }}
              >🗑</button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ─── Shared micro-components ─────────────────────────────────────────────────
function BottomSheet({ children, onClose }) {
  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ width: '100%', maxWidth: 520, background: 'white', borderRadius: '20px 20px 0 0', padding: '24px 20px 36px', maxHeight: '88vh', overflowY: 'auto', animation: 'slideInFromBottom .22s cubic-bezier(.4,0,.2,1)' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ width: 36, height: 4, background: '#E5E7EB', borderRadius: 2, margin: '0 auto 18px' }} />
        {children}
      </div>
    </div>
  );
}

function Badge({ bg, color, children }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 10, background: bg, color }}>
      {children}
    </span>
  );
}

function StatCard({ label, value, sub, color, bg, unit }) {
  return (
    <div style={{ flex: 1, background: bg, borderRadius: 10, padding: '10px 12px', borderLeft: `3px solid ${color}` }}>
      <div style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 900, color: '#111B21' }}>{value}</div>
      {(sub || unit) && <div style={{ fontSize: 10, color: '#667781', marginTop: 2 }}>{sub || unit}</div>}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #F3F4F6', fontSize: 13 }}>
      <span style={{ color: '#667781', fontWeight: 500 }}>{label}</span>
      <span style={{ color: '#111B21', fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{value}</span>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
      <div style={{ width: 28, height: 28, border: '3px solid #E5E7EB', borderTop: '3px solid #128C7E', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    </div>
  );
}

function EmptyState({ icon, title, sub }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>{icon}</div>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: '#374151', margin: '0 0 6px' }}>{title}</h3>
      <p style={{ fontSize: 13, color: '#9CA3AF', textAlign: 'center', margin: 0 }}>{sub}</p>
    </div>
  );
}
